import { useMemo, type ReactElement } from 'react'
import { ButtonSave, Page, PageHeading } from '@components'
import { shouldAskBracketQuestions, useUpdateGame, useTranslation } from '@front/model'
import { useToastError } from '@hooks'
import { type GameData } from '@terros/common'
import { CompetitionPageContainer } from '../competition/exports'
import { GamePermission } from '../competition_.$gameId/GamePermission'
import { useGameContext } from '../competition_.$gameId/useGameContext'
import { getGameMetadataTitle } from '../competition_.$gameId/util'
import { TimelineSetup } from './TimelineForm'
import { BracketQuestions } from './bracketQuestions'
import { useTimelineForm } from './form'
import { createFileRoute } from '@tanstack/react-router'

function CompetitionTimeline(): ReactElement {
  const { t } = useTranslation()
  const { data, refetch, isFetching } = useGameContext()
  const { update, isUpdating, error: updateError } = useUpdateGame()
  useToastError(updateError)

  const form = useTimelineForm()

  const handleSubmit = async (values: GameData): Promise<void> => {
    await update(values)
    form.resetDirty()
  }

  const shouldAskQuestions = useMemo(() => shouldAskBracketQuestions(form.values), [form])

  if (shouldAskQuestions) {
    return (
      <CompetitionPageContainer>
        <BracketQuestions data={data} form={form} />
      </CompetitionPageContainer>
    )
  }

  return (
    <form className='w-full' onSubmit={form.onSubmit(handleSubmit)}>
      <CompetitionPageContainer>
        <PageHeading
          isFetching={isFetching}
          metadata={{ title: getGameMetadataTitle(data.game, 'Timeline') }}
          refetch={refetch}
          title={t('Timeline')}
        >
          <GamePermission>
            <ButtonSave disabled={!form.isDirty()} loading={isUpdating} type='submit' />
          </GamePermission>
        </PageHeading>
        <Page.Content scrollArea>
          <TimelineSetup data={data} form={form} />
        </Page.Content>
      </CompetitionPageContainer>
    </form>
  )
}
export const Route = createFileRoute('/competition_/$gameId/timeline/')({
  component: CompetitionTimeline,
})
